<template>
  <div
    class="flex flex-wrap w-full pt-0 hero-min-height"
    :class="{ 'md:pt-12 hero-margin-top': isLoggedOut && !isMobileApp }"
    data-test-id="athlete-profile-hero"
  >
    <div
      class="relative flex flex-col justify-between w-full h-full text-center md:h-auto md:flex-row md:w-3/5 lg:w-1/2 hero-left md:mt-0"
      :class="{ 'md:mt-0 md:pt-0 hero-head-margin': (isLoggedOut && !isMobileApp) }"
    >
      <div class="absolute inset-0 overflow-hidden flex justify-center items-center">
        <img :src="backgroundImageUrl" class="absolute object-cover w-full h-full" />
        <div class="absolute inset-0" :style="backgroundStyle" />
      </div>
      <div
        class="relative z-10 flex flex-col items-center justify-center w-full"
      >
        <div
          class
          :class="{
            hidden: (!hasCoverVideo || !hasVideo) && !isCurrentUser,
          }"
        >
          <AthleteHeroVideoEmptyState
            v-if="!hasVideo && isCurrentUser"
          />
          <AthleteHeroFeaturedVideoEmptyState
            v-if="!hasCoverVideo && hasVideo && isCurrentUser"
          />
          <CoachHeroVideoEmptyState
            v-else-if="
              !hasVideo && isCollegeCoach && isOnIntegratedTeam
            "
          />
        </div>
        <AthleteCommittedBanner
          v-if="user.commitment"
          class
          :name="commitmentSchoolName"
          :image="commitmentSchoolImage"
          :rounded-top="hasCoverVideo"
        />

        <template v-if="hasCoverVideo">
          <div
            class="mt-6 mb-20 cursor-pointer md:my-0"
            @click="showVideoModal = true"
          >
            <img
              src="https://cdn.sportsrecruits.com/assets/itg/play-button-white-blue.svg"
              style="height: 86px"
            >
          </div>
        </template>
      </div>

      <div class="relative hidden md:block">
        <div
          class="triangle"
          @click.stop
        />
        <div class="absolute top-0 w-full h-full">
          <div class="flex flex-col justify-center h-full">
            <Avatar
              :size="160"
              :show-fav-interested="showFavInterested"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="z-30 flex flex-col justify-center w-full px-3 py-4 bg-white md:p-6 md:py-12 md:pl-12 md:w-2/5 lg:w-1/2 md:rounded-none md:mt-0"
      :style="isLoggedOut && `min-height: 286px;`"
      :class="{ 'rounded-t-lg -mt-3': hasCoverVideo && !user.commitment }"
    >
      <!-- Get Verified! -->
      <AthleteCompleteVerification />

      <div class="text-left">
        <div class="flex flex-row justify-between">
          <div class="md:hidden">
            <Avatar
              :size="64"
              :show-fav-interested="showFavInterested"
            />
          </div>
          <div class="flex flex-row">
            <SocialShare
              v-if="canShare"
              :link="athleteURL"
              :text="`Check out ${fullName}'s profile on ${siteName}:`"
              class="md:hidden"
            />
            <AthleteHeroTag
              v-if="showSport && sportName"
              :sport="sportName"
              :gender="user.gender"
              class="mx-1 text-gray-700 bg-gray-100 md:ml-0"
            />
            <Pill
              v-if="athleteIsInterestedInProgram"
              text="Interested"
              class="mr-1 text-white border-green-300"
              color="green"
              :has-icon="true"
            >
              <div slot="lead">
                <i class="sr-2 sr-v2-mortar-board mx-2" />
              </div>
            </Pill>
            <SocialShare
              v-if="canShare"
              :link="athleteURL"
              :text="`Check out ${fullName}'s profile on ${siteName}:`"
              class="hidden md:block"
            />
          </div>
        </div>
        <h1
          class="my-3 font-bold break-word ds-text-3xl text-gray-1000"
        >
          {{ fullName }}
          <VerifiedBadge
            v-if="user.isVerified"
            size="2xl"
          />
          <NotVerifiedBadge
            v-else-if="isCurrentUser"
            size="2xl"
          />
        </h1>

        <h3
          class="m-0 mb-4 font-semibold text-gray-500 ds-text-xl lg:ds-text-2xl"
        >
          {{ user.classYear }}
          <span v-if="user.isTransfer" data-test-id="transfer-tag">Transfer </span>
          <span v-if="positionList && hasUserInfo">•</span>
          {{ positionList }}
        </h3>
        <div class="hidden my-4 bg-gray-300 h-1px sm:block md:hidden" />
        <div class="flex flex-row justify-start mb-4">
          <AthleteHeroStat
            v-if="user.height"
            :value="user.height | formatHeight"
            label="Height"
            class="w-auto mr-8"
            :primary="true"
          />
          <AthleteHeroStat
            v-if="user.weight"
            :value="`${user.weight}lbs`"
            label="Weight"
            class="w-auto mr-8"
            :primary="true"
          />
          <AthleteHeroStat
            v-if="user.gpa && parseInt(user.gpa.value)"
            :value="`${user.gpa.value}/${user.gpa.scale}`"
            :label="`Gpa ${
              user.gpa.isWeighted
                ? '<span class=\'lowercase ds-text-2xs font-semibold text-gray-500\'>(weighted)</span>'
                : ''
            }`"
            class="w-auto"
            :primary="true"
          />
        </div>
        <div class="flex flex-col">
          <AthleteHeroStat
            v-if="clubList"
            :value="clubList"
            label="Club"
            class="mb-3"
          />
          <AthleteHeroStat
            v-if="highSchoolList"
            :value="highSchoolList"
            label="High School"
            class="mb-3"
          />
          <AthleteHeroStat
            v-if="user.city || user.state"
            :value="hometown"
            label="Home Town"
          />
        </div>
      </div>
    </div>

    <VideoModal
      v-if="hasCoverVideo"
      :show="showVideoModal"
      :video="coverVideo"
      @close="showVideoModal = false"
    />
  </div>
</template>

<script>
import AthleteHeroStat from "./AthleteHeroStat"
import AthleteHeroTag from "./AthleteHeroTag"
import VerifiedBadge from "@/components/Verification/VerifiedBadge"
import VideoModal from "@/components/VideoModal/Modal"
import NotVerifiedBadge from "@/components/Verification/NotVerifiedBadge"
import AthleteCompleteVerification from "./AthleteCompleteVerificationBanner"
import Avatar from "./Avatar"
import { mapState, mapGetters } from "vuex"
import AthleteCommittedBanner from "./AthleteCommittedBanner"
import AthleteHeroVideoEmptyState from "./EmptyStates/AthleteHeroVideoEmptyState"
import AthleteHeroFeaturedVideoEmptyState from "./EmptyStates/AthleteHeroFeaturedVideoEmptyState"
import CoachHeroVideoEmptyState from "./EmptyStates/CoachHeroVideoEmptyState"
import SocialShare from "@/components/SocialShare"
import { Pill } from '@sportsrecruits/playbook'
import { makeAbsoluteUrl } from "@/utils/makeAbsoluteUrl"
import SRCookies from "@/services/cookies";

export default {
    components: {
        AthleteHeroStat,
        AthleteHeroTag,
        VerifiedBadge,
        Avatar,
        VideoModal,
        Pill,
        NotVerifiedBadge,
        AthleteCommittedBanner,
        AthleteHeroVideoEmptyState,
        AthleteHeroFeaturedVideoEmptyState,
        CoachHeroVideoEmptyState,
        SocialShare,
        AthleteCompleteVerification,
    },

    props: {
        showSport: { required: false, default: true, type: Boolean },
        showFavInterested: { required: false, default: true, type: Boolean },
        showLinks: { required: false, default: true, type: Boolean },
    },

    data() {
        return {
            athlete: {},
            showVideoModal: false,
        }
    },

    computed: {
        siteName() {
            return this.$domain.config('title_name', 'SportsRecruits')
        },
        athleteURL() {
            return window.location.href
        },
        hasVideo() {
            return this.videos && !!this.videos.length
        },
        coverVideo() {
            return this.user.coverVideo
        },

        hasCoverVideo() {
            return !!this.coverVideo
        },

        isMobileApp() {
          return SRCookies.isMobileSession()
        },

        useCoverVideoImage() {
            return (
                this.hasCoverVideo &&
                this.coverVideo.host !== "hudl" &&
                this.coverVideo.host !== "krossover" &&
                (!this.coverVideo.durationInMilliseconds ||
                    this.coverVideo.durationInMilliseconds > 5000)
            )
        },

        backgroundImageUrl() {
            return this.useCoverVideoImage
                ? this.coverVideo.thumbnailUrl
                : this.sportHeroImage
        },

        backgroundStyle() {
            return {
                background: `linear-gradient(to top, rgb(20, 28, 33) 0px, rgba(33, 44, 53, 0.8) 100%)`,
            }
        },
        hasClassYear() {
            return !!this.user.classYear
        },
        hasUserInfo() {
            return this.hasClassYear || this.user.isTransfer
        },
        clubList: function() {
            if (this.allClubAffiliations && this.allClubAffiliations.length) {
                return this.allClubAffiliations
                    .map((aff, index) => {
                        if (aff.isIntegrated && this.showLinks) {
                            const absoluteUrl = makeAbsoluteUrl(`/organization/${aff.organization.urlSlug}`)
                            return `<a href="${absoluteUrl}">${aff.organization.name}</a>`
                        }
                        return aff.organization && aff.organization.name
                    })
                    .join(", ")
            }

            return null
        },
        highSchoolList: function() {
            if (
                this.allHighSchoolAffiliations &&
                this.allHighSchoolAffiliations.length
            ) {
                return this.allHighSchoolAffiliations
                    .map((aff, index) => {
                        if (aff.isIntegrated && this.showLinks) {
                            return `<a href="/organization/${aff.organization.urlSlug}">${aff.organization.name}</a>`
                        }
                        return aff.organization && aff.organization.name
                    })
                    .join(", ")
            }

            return null
        },
        ...mapState({
            user: state => state.athleteProfile.user,
        }),
        ...mapGetters({
            profilePhotoUrl: "athleteProfile/userProfilePhotoUrl",
            fullName: "athleteProfile/userFullName",
            positionList: "athleteProfile/allPositionsString",
            hometown: "athleteProfile/homeTownString",
            isCurrentUser: "athleteProfile/isCurrentUser",
            commitmentSchoolImage: "athleteProfile/commitmentSchoolImage",
            commitmentSchoolName: "athleteProfile/commitmentSchoolName",
            athleteIsInterestedInProgram:
                "athleteProfile/athleteIsInterestedInProgram",
            sportName: "athleteProfile/sportName",
            sportHeroImage: "athleteProfile/sportHeroImage",
            isCollegeCoach: "userData/isCollegeCoach",
            isOnIntegratedTeam: "athleteProfile/isOnIntegratedTeam",
            canShare: "athleteProfile/canShare",
            allClubAffiliations: "athleteProfile/allClubAffiliations",
            allHighSchoolAffiliations:
                "athleteProfile/allHighSchoolAffiliations",
            isLoggedOut: "userData/isLoggedOut",
            videos: "athleteProfile/videos",
        }),
    },
}
</script>

<style scoped>
.hero-left {
    background-position: center;
    background-size: cover;
}

/* .triangle {
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 479px 160px;
  border-color: transparent transparent white transparent;
} */

.triangle {
    width: 160px;
    height: 100%;
    background: white;
    /* transform: skewX(-12deg) translateX(80px); */
    transform: skewX(-15deg) translateX(80px);
}

.w-160 {
    width: 160px;
}

.hero-head-margin {
    margin-top: 0px;
}
@media screen and (max-width: 829px) {
    .hero-head-margin {
        margin-top: 97px;
    }
}

.hero-margin-top {
    margin-top: 30px;
}

@media screen and (max-width: 829px) {
    .hero-margin-top {
        margin-top: 0px;
    }
}

.hero-min-height {
    min-height: 380px;
}

@media screen and (max-width: 768px) {
    .hero-min-height {
        min-height: 0px;
    }
}
</style>
